import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, ClipboardList, CheckCircle2, ImagePlus, X, Loader2 } from "lucide-react";
import { useEstimate } from "../context/EstimateContext";
import { SERVICE_OPTIONS, BUDGET_OPTIONS, TIMELINE_OPTIONS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Project", "Details", "Contact"];

const empty = {
  name: "", email: "", phone: "", address: "",
  service_type: "", project_scope: "", budget: "", timeline: "", details: "", referral: "",
  photos: [], company_website: "",
};

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-sm border px-4 py-2.5 text-sm font-medium text-left transition-colors ${
      active
        ? "bg-[var(--apex-accent)] border-[var(--apex-accent)] text-white"
        : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"
    }`}
  >
    {children}
  </button>
);

export const EstimateWidget = () => {
  const { open, setOpen, openEstimate } = useEstimate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (form.photos.length + files.length > 8) {
      toast.error("You can attach up to 8 photos.");
      return;
    }
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is over 10MB.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/estimates/upload`, fd);
        setForm((f) => ({ ...f, photos: [...f.photos, data] }));
      } catch (e) {
        toast.error(`Couldn't upload ${file.name}.`);
      }
    }
    setUploading(false);
  };

  const removePhoto = (path) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((p) => p.path !== path) }));

  const reset = () => { setForm(empty); setStep(0); setDone(false); };

  const canNext = () => {
    if (step === 0) return Boolean(form.service_type);
    if (step === 1) return true;
    return form.name && form.email && form.phone;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setDone(true);
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          currency: "USD",
          service_type: form.service_type,
        });
      }
      toast.success("Estimate request sent! We'll be in touch shortly.");
    } catch (e) {
      toast.error("Something went wrong. Please call us at (484) 632-5956.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating widget — right-edge, vertically centered so it never collides with the bottom HomeGuide bar */}
      <button
        data-testid="floating-estimate-widget"
        onClick={openEstimate}
        aria-label="Get a free estimate"
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] group inline-flex items-center gap-2 rounded-l-full bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] hover:pr-7 text-white font-bold text-sm sm:text-base pl-4 pr-5 py-3.5 shadow-2xl shadow-orange-900/40 transition-all"
      >
        <ClipboardList size={20} className="shrink-0" />
        <span className="whitespace-nowrap">Free Estimate</span>
      </button>

      <Dialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(reset, 200); }}
      >
        <DialogContent
          data-testid="estimate-dialog"
          className="max-w-lg p-0 gap-0 overflow-hidden bg-white"
        >
          <DialogDescription className="sr-only">
            Request a free home remodeling estimate from Apex Home Pro Inc.
          </DialogDescription>
          {/* Honeypot: hidden from users, catches bots */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.company_website}
            onChange={(e) => set("company_website", e.target.value)}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          {done ? (
            <div className="p-10 text-center" data-testid="estimate-success">
              <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-green-100 text-green-600 mb-5">
                <CheckCircle2 size={34} />
              </div>
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl text-[var(--apex-primary)]">Request received!</DialogTitle>
              </DialogHeader>
              <p className="text-slate-600 mt-3">
                Thanks {form.name.split(" ")[0]}. The Apex team will review your project and reach
                out within one business day. Need us sooner? Call <a href="tel:+14846325956" className="text-[var(--apex-accent)] font-semibold">(484) 632-5956</a>.
              </p>
              <button
                data-testid="estimate-close-success"
                onClick={() => { setOpen(false); setTimeout(reset, 200); }}
                className="mt-7 rounded-sm bg-[var(--apex-primary)] text-white font-semibold px-7 py-3"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="bg-[var(--apex-primary)] px-6 pt-6 pb-5">
                <DialogHeader>
                  <DialogTitle className="font-display font-black text-2xl text-white text-left">
                    Get your free estimate
                  </DialogTitle>
                </DialogHeader>
                <p className="text-slate-300 text-sm mt-1">Step {step + 1} of 3 · {STEPS[step]}</p>
                <Progress value={((step + 1) / 3) * 100} className="mt-4 h-1.5 bg-white/15" />
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto apex-scroll">
                {step === 0 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-slate-700 font-semibold">What do you need done?</Label>
                      <div className="grid grid-cols-2 gap-2.5 mt-3">
                        {SERVICE_OPTIONS.map((s) => (
                          <Chip key={s} testid={`service-opt-${s}`} active={form.service_type === s} onClick={() => set("service_type", s)}>{s}</Chip>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="scope" className="text-slate-700 font-semibold">Rough project scope (optional)</Label>
                      <Input id="scope" data-testid="estimate-scope" value={form.project_scope} onChange={(e) => set("project_scope", e.target.value)} placeholder="e.g. 12x14 kitchen, new cabinets & counters" className="h-12 mt-2" />
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-slate-700 font-semibold">Estimated budget</Label>
                      <div className="grid grid-cols-2 gap-2.5 mt-3">
                        {BUDGET_OPTIONS.map((b) => (
                          <Chip key={b} testid={`budget-opt-${b}`} active={form.budget === b} onClick={() => set("budget", b)}>{b}</Chip>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700 font-semibold">Ideal timeline</Label>
                      <div className="grid grid-cols-2 gap-2.5 mt-3">
                        {TIMELINE_OPTIONS.map((t) => (
                          <Chip key={t} testid={`timeline-opt-${t}`} active={form.timeline === t} onClick={() => set("timeline", t)}>{t}</Chip>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="details" className="text-slate-700 font-semibold">Anything else we should know?</Label>
                      <Textarea id="details" data-testid="estimate-details" value={form.details} onChange={(e) => set("details", e.target.value)} placeholder="Materials, style preferences, deadlines..." className="mt-2 min-h-[90px]" />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-semibold">Photos of the space (optional)</Label>
                      <p className="text-xs text-slate-500 mt-1 mb-2">Show us the room, damage, or inspiration — up to 8 images.</p>
                      <label
                        data-testid="estimate-photo-dropzone"
                        className="flex flex-col items-center justify-center gap-1.5 rounded-sm border-2 border-dashed border-slate-300 hover:border-[var(--apex-accent)] bg-[var(--apex-surface)] py-6 cursor-pointer transition-colors"
                      >
                        {uploading ? (
                          <Loader2 size={22} className="text-[var(--apex-accent)] animate-spin" />
                        ) : (
                          <ImagePlus size={22} className="text-[var(--apex-accent)]" />
                        )}
                        <span className="text-sm font-medium text-slate-600">{uploading ? "Uploading..." : "Tap to add photos"}</span>
                        <input
                          data-testid="estimate-photo-input"
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                        />
                      </label>
                      {form.photos.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mt-3" data-testid="estimate-photo-preview">
                          {form.photos.map((p) => (
                            <div key={p.path} className="relative group aspect-square rounded-sm overflow-hidden ring-1 ring-slate-200">
                              <img src={`${API}/files/${p.path}`} alt={p.filename} className="h-full w-full object-cover" />
                              <button
                                type="button"
                                data-testid="estimate-photo-remove"
                                onClick={() => removePhoto(p.path)}
                                className="absolute top-1 right-1 grid place-items-center h-5 w-5 rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Remove photo"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-semibold">Full name *</Label>
                      <Input id="name" data-testid="estimate-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Homeowner" className="h-12 mt-2" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-semibold">Email *</Label>
                        <Input id="email" type="email" data-testid="estimate-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="h-12 mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-semibold">Phone *</Label>
                        <Input id="phone" data-testid="estimate-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(484) 000-0000" className="h-12 mt-2" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="address" className="text-slate-700 font-semibold">Project address</Label>
                      <Input id="address" data-testid="estimate-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, City, ZIP" className="h-12 mt-2" />
                    </div>
                    <div>
                      <Label htmlFor="referral" className="text-slate-700 font-semibold">How did you hear about us?</Label>
                      <Input id="referral" data-testid="estimate-referral" value={form.referral} onChange={(e) => set("referral", e.target.value)} placeholder="Google, referral, social..." className="h-12 mt-2" />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4 bg-white">
                <button
                  data-testid="estimate-back-btn"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-0 transition-colors"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                {step < 2 ? (
                  <button
                    data-testid="estimate-next-btn"
                    onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please pick a service to continue.")}
                    className="inline-flex items-center gap-2 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white font-semibold px-6 py-3 transition-colors"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    data-testid="estimate-submit-btn"
                    onClick={() => canNext() ? submit() : toast.error("Please fill name, email and phone.")}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white font-semibold px-6 py-3 transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : <>Submit request <Check size={16} /></>}
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
